import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Badge, Button, PageHeader, EngineKeyline } from '@/components/ui/primitives';
import { ErrorState, LoadingState } from '@/components/DataState';
import { QuickUpdateSheet } from '@/components/leads/QuickUpdateSheet';
import { LeadEditSheet } from '@/components/leads/LeadEditSheet';
import { fetchLeadFull } from '@/data/leads';
import { leadCardModel, describeChange, displayHost, todayIso, callActionAllowed, type LeadEventRow, type LeadRecord } from '@/data/leads-model';
import { useIsDesktopPointer } from '@/hooks/useMediaQuery';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { formatRelative } from '@/lib/utils';
import { CategoryChips } from '@/components/leads/CategoryChips';

/* /sales/leads/:id — the lead dossier: every imported prospect field, the
 * booking-platform EVIDENCE (what we saw, where), the links the workbook
 * carried (Maps / search / source / listings), notes, the History (audit
 * timeline incl. per-field changes), the quick-update controls and — for the
 * owner — the full edit of any field. :id is the lead_ref. Reads
 * GET /outreach/leads/{ref} (whitelisted projection — never internal columns). */

export function LeadDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { activeWorkspace } = useAuth();
  const { active } = useActiveDomain();
  const [quick, setQuick] = useState(false);
  const [editing, setEditing] = useState(false);
  const isOwner = activeWorkspace?.role === 'owner';
  const showCall = callActionAllowed(useIsDesktopPointer()); // owner: no Call (tel:) action on desktop for now; the number stays visible
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['lead-full', id],
    queryFn: () => fetchLeadFull(id ?? ''),
    enabled: !!id,
  });

  if (isPending) return <LoadingState rows={6} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) {
    return (
      <div className="py-16 text-center text-text-secondary">
        Lead not found. <Link to="/sales/leads" className="text-engine underline">Back to leads</Link>
      </div>
    );
  }
  const { lead, events } = data;
  const m = leadCardModel(lead, todayIso());

  const onSaved = (updated: LeadRecord) => {
    queryClient.setQueryData(['lead-full', id], { lead: updated, events });
    void queryClient.invalidateQueries({ queryKey: ['lead-full', id] });
    void queryClient.invalidateQueries({ queryKey: ['leads-page'] });
  };

  return (
    <div data-testid="lead-detail">
      <nav className="mb-3 text-xs text-text-tertiary">
        <Link to="/sales/leads" className="hover:text-text-primary">Leads</Link> › <span className="text-text-secondary">{m.title}</span>
      </nav>

      <PageHeader
        title={m.title}
        subtitle={[m.category, m.area, m.postcode].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            {isOwner ? <Button variant="primary" onClick={() => setEditing(true)} data-testid="edit-details">Edit details</Button> : null}
            <Button variant={isOwner ? 'secondary' : 'primary'} onClick={() => setQuick(true)}>Quick update</Button>
          </div>
        }
      />

      {/* Action row — thumb-reachable on mobile */}
      <div className="mb-4 grid grid-cols-4 gap-2 lg:hidden">
        <Action href={m.actions.website} label="Website" />
        <Action href={showCall ? m.actions.tel : null} label="Call" />
        <Action href={m.actions.mail} label="Email" />
        <Action href={m.actions.maps} label="Maps" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card className="overflow-hidden">
            <EngineKeyline />
            <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3">
              <Field label="Categories" className="col-span-2 sm:col-span-3">
                <span data-testid="lead-categories">
                  <CategoryChips names={m.categories} max={12} />
                  <span className="mt-1 block text-[11px] text-text-tertiary">{lead.categories_source === 'manual' ? 'Set by hand' : `Automatic — from the business type${lead.modality ? ` "${lead.modality}"` : ''} and the business name`}</span>
                </span>
              </Field>
              <Field label="Sales status"><Badge tone={lead.status === 'DISCOVERED' ? 'neutral' : 'compliance'}>{m.statusLabel}</Badge></Field>
              <Field label="Visit approach">{m.approachLabel ?? '—'}</Field>
              <Field label="Next follow-up">{m.followUp ?? '—'}{m.followUpOverdue ? <span className="ml-1 text-danger">overdue</span> : null}</Field>
              <Field label="Priority">{m.priority ? `Priority ${m.priority}` : '—'}</Field>
              <Field label="Day / route">{m.routeDay ?? '—'}</Field>
              <Field label="Visit date">{lead.visit_date ?? '—'}</Field>
              <Field label="Phone">{lead.phone ? (showCall && m.actions.tel ? <a className="text-engine underline" href={m.actions.tel}>{lead.phone}</a> : <span className="select-all" data-testid="phone-plain">{lead.phone}</span>) : '—'}</Field>
              <Field label="Email">{m.email ? (m.actions.mail ? <a className="select-all break-all text-engine underline" href={m.actions.mail}>{m.email}</a> : <span className="select-all break-all" title="Not contactable by email">{m.email}</span>) : '—'}</Field>
              <Field label="Website">{m.actions.website ? <a className="break-all text-engine underline" href={m.actions.website} target="_blank" rel="noopener noreferrer">{displayHost(lead.website)}</a> : '—'}</Field>
              <Field label="Practitioner / contact">{lead.practitioner_name ?? '—'}</Field>
              <Field label="Address" className="col-span-2 sm:col-span-2">{lead.address ?? '—'}</Field>
              <Field label="Decision-maker" className="col-span-2 sm:col-span-3">{lead.decision_maker ?? '—'}</Field>
              <Field label="Socials" className="col-span-2 sm:col-span-3">
                {[lead.instagram_handle, lead.facebook_url, lead.linkedin_url, lead.other_social].some(Boolean)
                  ? <span className="break-all">{[lead.instagram_handle, lead.facebook_url ? displayHost(lead.facebook_url) : null, lead.linkedin_url ? displayHost(lead.linkedin_url) : null, lead.other_social].filter(Boolean).join(' · ')}</span>
                  : '—'}
              </Field>
              <Field label="Subscribed?">{yesNo(lead.subscribed)}</Field>
              <Field label="Content shoot booked?">{yesNo(lead.content_shoot_booked)}</Field>
              <Field label="Campaign">{lead.campaign ?? '—'}</Field>
              <Field label="Next action" className="col-span-2 sm:col-span-3">{lead.next_action ?? '—'}</Field>
            </div>
          </Card>

          {/* Booking platform evidence */}
          <Card className="p-4" data-testid="lead-evidence">
            <h2 className="mb-2 text-sm font-semibold text-text-primary">Current booking platform</h2>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-medium text-text-primary">{m.platform}</span>
              <Badge tone={m.platformKind === 'confirmed_system' ? 'compliance' : m.platformKind === 'direct_website' || m.platformKind === 'in_house_system' ? 'success' : m.platformKind === 'directory_listing' ? 'warning' : 'neutral'}>{m.platformKindLabel}</Badge>
            </div>
            <dl className="mt-3 space-y-2 text-sm">
              <div><dt className="text-[11px] uppercase tracking-wide text-text-tertiary">Verification</dt><dd className="text-text-secondary">{m.evidence.verification ?? '—'}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wide text-text-tertiary">Evidence note</dt><dd className="text-text-secondary">{m.evidence.note ?? '—'}</dd></div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-text-tertiary">Evidence link</dt>
                <dd>{m.evidence.url ? <a className="break-all text-engine underline" href={m.evidence.url} target="_blank" rel="noopener noreferrer">{m.evidence.url}</a> : <span className="text-text-tertiary">None — ask on visit</span>}</dd>
              </div>
              {m.evidence.listings.length ? (
                <div>
                  <dt className="text-[11px] uppercase tracking-wide text-text-tertiary">Public listings</dt>
                  <dd className="space-y-1">{m.evidence.listings.map((u) => <a key={u} className="block break-all text-engine underline" href={u} target="_blank" rel="noopener noreferrer">{u}</a>)}</dd>
                </div>
              ) : null}
            </dl>
            <p className="mt-3 text-[11px] text-text-tertiary">
              A marketplace page (Treatwell / Fresha) is not proof of the booking system the business runs. "Unknown" stays Unknown until confirmed on the visit — then set the kind with Edit details.
            </p>
          </Card>

          {/* Links + notes */}
          <Card className="p-4">
            <h2 className="mb-2 text-sm font-semibold text-text-primary">Links</h2>
            <div className="flex flex-wrap gap-2 text-sm">
              {lead.links?.maps ? <a className="rounded-md border border-border-default px-3 py-1.5 text-text-primary hover:bg-hover" href={lead.links.maps} target="_blank" rel="noopener noreferrer">Open Google Maps</a> : null}
              {lead.links?.search ? <a className="rounded-md border border-border-default px-3 py-1.5 text-text-primary hover:bg-hover" href={lead.links.search} target="_blank" rel="noopener noreferrer">Search on Google</a> : null}
              {lead.links?.source ? <a className="rounded-md border border-border-default px-3 py-1.5 text-text-primary hover:bg-hover" href={lead.links.source} target="_blank" rel="noopener noreferrer">Business source</a> : null}
              {!lead.links ? <span className="text-text-tertiary">No links on file</span> : null}
            </div>
            <h2 className="mb-1 mt-4 text-sm font-semibold text-text-primary">Outcome / current pain</h2>
            <p className="whitespace-pre-wrap text-sm text-text-secondary">{lead.outcome_note ?? '—'}</p>
            <h2 className="mb-1 mt-4 text-sm font-semibold text-text-primary">Notes</h2>
            <p className="whitespace-pre-wrap text-sm text-text-secondary">{lead.fit_notes ?? '—'}</p>
          </Card>

          <Card className="p-4" data-testid="lead-history">
            <h2 className="mb-3 text-sm font-semibold text-text-primary">History</h2>
            {events.length ? (
              <ol className="space-y-3 border-l border-border-subtle">
                {events.map((ev, i) => <TimelineItem key={`${ev.event_at}-${i}`} ev={ev} />)}
              </ol>
            ) : (
              <p className="text-sm text-text-tertiary">No events yet.</p>
            )}
          </Card>
        </div>

        <div className="space-y-3">
          <div className="rounded-lg border border-border-subtle bg-surface p-4 text-xs text-text-tertiary">
            <div className="mb-1 font-semibold text-text-secondary">Provenance</div>
            <div>Ref {lead.lead_ref}</div>
            {lead.import_row_ref ? <div>Workbook row {lead.import_row_ref.split(':').pop()}</div> : null}
            <div>Source: {lead.source_directory ?? '—'}</div>
            <div>Captured via: {lead.captured_via ?? '—'}</div>
            {lead.created_at ? <div>Added {formatRelative(lead.created_at)}</div> : null}
            {lead.updated_at ? <div>Updated {formatRelative(lead.updated_at)}</div> : null}
          </div>
        </div>
      </div>

      <QuickUpdateSheet lead={lead} open={quick} onClose={() => setQuick(false)} onSaved={onSaved} />
      <LeadEditSheet lead={lead} open={editing} onClose={() => setEditing(false)} onSaved={onSaved} projectId={active?.projectId ?? lead.project_id} />
    </div>
  );
}

function yesNo(v: boolean | null): string { return v === null ? '—' : v ? 'Yes' : 'No'; }

function Action({ href, label }: { href: string | null; label: string }) {
  return href ? (
    <a href={href} target={/^(tel|mailto):/i.test(href) ? undefined : '_blank'} rel="noopener noreferrer" className="flex h-11 min-w-0 items-center justify-center truncate rounded-md border border-border-default bg-surface px-1 text-sm font-medium text-text-primary">{label}</a>
  ) : (
    <span className="flex h-11 items-center justify-center rounded-md border border-dashed border-border-subtle text-sm text-text-tertiary">{label}</span>
  );
}

const EVENT_LABEL: Record<string, string> = {
  FIELD_UPDATED: 'Edited', IMPORTED: 'Imported', IMPORT_MERGED: 'Import merged', CAPTURED: 'Captured', CAPTURE_MERGED: 'Capture merged',
  CAPTURE_DUPLICATE: 'Capture duplicate', DISCOVERED: 'Discovered', PROMOTED: 'Promoted',
};

function TimelineItem({ ev }: { ev: LeadEventRow }) {
  const changes = ev.changes ?? null;
  return (
    <li className="relative pl-6">
      <span className="absolute left-1.5 top-1.5 h-2 w-2 rounded-full bg-engine" aria-hidden />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-text-primary">{EVENT_LABEL[ev.event_type] ?? ev.event_type}</span>
        <span className="text-[11px] text-text-tertiary">{ev.event_at}</span>
      </div>
      {changes && changes.length ? (
        <ul className="mt-1 space-y-0.5 text-sm" data-testid="history-changes">
          {changes.map((c, i) => {
            const d = describeChange(c);
            return (
              <li key={`${c.field}-${i}`} className="break-words text-text-secondary">
                <span className="text-text-primary">{d.label}:</span> <span className="line-through decoration-text-tertiary/60">{d.from}</span> → <span className="text-text-primary">{d.to}</span>
              </li>
            );
          })}
        </ul>
      ) : ev.detail ? <p className="break-words text-sm text-text-secondary">{ev.detail}</p> : null}
    </li>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 text-sm text-text-primary">{children}</div>
    </div>
  );
}
